/**
 * The one Axios instance every call to the Grow backend goes through.
 *
 * Who uses it: every module in src/shared/api, and features/tips/api and
 * features/plant-list/lib/shareFrames. Nothing else should call the backend
 * with its own axios or fetch, or it misses the token, the garden header and
 * the wall below.
 *
 * Base URL: VITE_API_URL exactly as the build was given it, which already
 * ends in /api (http://localhost:3001/api locally; production's is
 * PRODUCTION_API_URL in config/tunables/shared). So the paths in the api
 * modules are written without it ('/plants', '/billing/status'), and adding
 * '/api' to one would ask for /api/api/plants. It should be set: unlike
 * config/env-vars this reads import.meta.env itself, with no production
 * default. Unset, it falls back to the local backend (LOCAL_API_URL,
 * http://localhost:3001/api, the same base as development) and, in
 * development, warns in the console.
 *
 * Every request (the request interceptor) carries:
 * - `Authorization: Bearer <Firebase ID token>` for the signed-in person
 *   (auth.currentUser.getIdToken(), which the SDK caches and renews by
 *   itself). With nobody signed in the request goes without it: the public
 *   endpoints (share and garden pages, gift codes, the email links' tokens)
 *   need none, and the private ones answer 401.
 * - `X-Grow-Garden: <owner's user id>` while this browser tends somebody
 *   else's garden (gardenMembers.chosenGarden). The backend reads and writes
 *   garden data as that owner's and ignores the header on what is personal
 *   (profile, preferences, billing).
 * - `X-Grow-View-As: <gardener's id>` while an admin is viewing as a gardener
 *   (shared/api/viewAs); a refused change (VIEW_AS_READ_ONLY) shows the server's sentence;
 *   a refused view clears it. Not sent on /admin/* requests or the admin pages.
 * - In development only, `x-dev-firebase-uid` (both spellings) with the
 *   Firebase uid, for the backend's dev logging. The dev log of the headers
 *   shows the bearer token masked. With VITE_USE_MOCK_AUTH=true
 *   the token is the fixed 'mock-token-123' and the uid VITE_DEV_FIREBASE_UID,
 *   and Firebase is not asked at all (the local walk-test setup); vite.config
 *   refuses to build for production with that flag on.
 *
 * Every response (the response interceptor):
 * - A photo the safety check flagged is filled with the "being reviewed"
 *   placeholder (lib/underReview) before any screen sees it.
 * - 402 SUBSCRIPTION_REQUIRED, FREE_PLANT_LIMIT and SUBSCRIPTION_REQUIRED_AI
 *   fire SUBSCRIPTION_REQUIRED_EVENT on window (detail: none, { kind:
 *   'plants', limit }, or { ai: true }); the layout's SubscriptionWall
 *   answers it, so the screen that made the call need not.
 * - 403 NOT_A_MEMBER sends the browser back to the person's own garden
 *   (gardenMembers.leaveRefusedGarden).
 * - Every failure is logged with console.warn (console.error goes to Sentry)
 *   and rethrown unchanged for its caller; lib/errorMessage turns it into
 *   words.
 *
 * Timeout: REQUEST_TIMEOUT_MS (30 s); the plant check-up passes a longer one.
 *
 * Nothing is cached here. React Query (config/queryClient) keeps the
 * answers; src/shared/README.md describes the keys.
 *
 * @module apiClient
 */

import axios from 'axios';
import { GARDEN_HEADER, chosenGarden, leaveRefusedGarden } from './gardenMembers';
import { auth } from '@/config/firebase';
import { debugLog } from '@/config/config';
// The event's name from the tunables rather than './billing', which imports this file.
import { LOCAL_API_URL, REQUEST_TIMEOUT_MS } from '@/config/tunables/shared';
import { SUBSCRIPTION_REQUIRED_EVENT } from '@/shared/lib/subscriptionEvent';
import { fillUnderReview } from '@/shared/lib/underReview';
import { VIEW_AS_HEADER, endRefusedViewAs, isAutomaticWrite, sendsViewAs, viewingAs } from './viewAs';
import { toast } from '@/shared/components/ui/use-toast';
import { t } from '@/shared/i18n/i18nSetup';

/**
 * Base URL for API requests: VITE_API_URL as built, ending in /api
 * (http://localhost:3001/api for local development). Read straight from
 * import.meta.env (config/env-vars' production default does not apply
 * here); unset, the local backend's (LOCAL_API_URL), with a warning in
 * development.
 */
const baseURL: string = import.meta.env.VITE_API_URL || LOCAL_API_URL;
if (!import.meta.env.VITE_API_URL && import.meta.env.DEV) {
  console.warn(`VITE_API_URL is not set; the API client is using ${LOCAL_API_URL}.`);
}

/**
 * Request headers as the dev log shows them: the bearer token masked, so a
 * console shared for help does not hand out a sign-in.
 *
 * @param headers - The request's headers.
 * @returns A copy with Authorization's token replaced.
 */
const maskedHeaders = (headers: Record<string, unknown>): Record<string, unknown> => {
  const shown = { ...headers };
  for (const name of Object.keys(shown)) {
    if (name.toLowerCase() === 'authorization' && typeof shown[name] === 'string') shown[name] = 'Bearer ***';
  }
  return shown;
};

/**
 * Flag to enable mock authentication for development/testing
 */
const USE_MOCK_AUTH = import.meta.env.VITE_USE_MOCK_AUTH === 'true';

/**
 * Development Firebase UID for mock authentication
 */
const DEV_FIREBASE_UID = import.meta.env.VITE_DEV_FIREBASE_UID;

debugLog('ApiClient', 'API Client - Using base URL:', baseURL);
debugLog('ApiClient', 'API Client - Mock Auth Enabled:', USE_MOCK_AUTH);
debugLog('ApiClient', 'API Client - Using Firebase UID for development:', DEV_FIREBASE_UID);

/*
 * The timeout (REQUEST_TIMEOUT_MS, in the shared tunables).
 *
 * Axios defaults to no timeout at all, which means a stalled request never
 * comes back: the button that started it keeps its spinner, the dialog stays
 * open, and the app looks broken in the one way that offers the user nothing
 * to do. Thirty seconds is longer than any call here legitimately takes —
 * uploads included — and a timeout raises an error the calling code already
 * knows how to show.
 */

/**
 * The configured Axios instance: base URL, JSON by default (a FormData body
 * sets its own multipart type), the timeout, and the two interceptors below.
 */
export const apiClient = axios.create({
  baseURL,
  timeout: REQUEST_TIMEOUT_MS,
  headers: {
    'Content-Type': 'application/json',
  }
});

/**
 * Request interceptor: adds the garden header, then the Firebase token (or
 * the mock token when mock sign-in is on). It never fails a request itself:
 * a token it cannot get is logged and the request goes without one, for the
 * backend to refuse.
 */
apiClient.interceptors.request.use(
  async (config) => {
    try {
      debugLog('ApiClient', 'API Request to:', config.url);

      /*
       * Which garden this is about, when it is not the signer's own.
       *
       * Read here rather than passed by each caller: a person tending a
       * household's garden is tending it in every request they make, and
       * one place that remembers is far safer than a hundred that might
       * forget. The backend ignores it on anything personal.
       */
      const garden = chosenGarden();
      if (garden !== null) config.headers[GARDEN_HEADER] = String(garden);
      // An admin viewing as a gardener (E16): every request says whose view it is.
      const viewing = viewingAs();
      if (viewing && sendsViewAs(config.url)) config.headers[VIEW_AS_HEADER] = String(viewing.id);
      // A write the page fires by itself is dropped quietly while viewing: answered as done, never sent.
      if (viewing && isAutomaticWrite(config.method, config.url, config.data)) {
        config.adapter = async () => ({ data: { success: true }, status: 200, statusText: 'OK', headers: {}, config });
      }
      
      if (USE_MOCK_AUTH) {
        debugLog('ApiClient', 'MOCK AUTH: Using mock token for API request');
        config.headers['Authorization'] = 'Bearer mock-token-123';
        
        if (import.meta.env.DEV) {
          config.headers['x-dev-firebase-uid'] = DEV_FIREBASE_UID;
          debugLog('ApiClient', 'Added Firebase UID header for mock auth:', DEV_FIREBASE_UID);
        }
        
        return config;
      }
      
      const user = auth.currentUser;
      
      if (user) {
        debugLog('ApiClient', 'Firebase user found for API request:', {
          uid: user.uid,
          email: user.email,
          displayName: user.displayName,
        });
        
        try {
          /*
           * getIdToken() without the force flag.
           *
           * This asked Firebase for a brand new token on every single request,
           * which put a round-trip to Google in front of every call the app
           * makes — and made every one of them fail, or hang, whenever that
           * round-trip did. The SDK already refreshes on its own when the
           * cached token is within five minutes of expiring, so the only thing
           * forcing bought was the outage.
           */
          const token = await user.getIdToken();
          config.headers['Authorization'] = `Bearer ${token}`;
          
          // In development, add the Firebase UID header in lowercase for consistent matching
          if (import.meta.env.DEV) {
            // Add in both formats to ensure one works
            config.headers['x-dev-firebase-uid'] = user.uid;
            config.headers['X-Dev-Firebase-UID'] = user.uid;
            debugLog('ApiClient', 'Dev mode: Added Firebase UID header:', user.uid);
            debugLog('ApiClient', 'Full request headers:', maskedHeaders(config.headers.toJSON()));
          }
          
          debugLog('ApiClient', 'Added Firebase token (first 20 chars):', token.substring(0, 20) + '...');
        } catch (tokenError) {
          console.error('Error getting fresh ID token:', tokenError);
          
          const token = await user.getIdToken(true);
          config.headers['Authorization'] = `Bearer ${token}`;
          
          // Also add headers in fallback case
          if (import.meta.env.DEV) {
            config.headers['x-dev-firebase-uid'] = user.uid;
            config.headers['X-Dev-Firebase-UID'] = user.uid;
            debugLog('ApiClient', 'Dev mode (fallback): Added Firebase UID header:', user.uid);
          }
          
          debugLog('ApiClient', 'Added non-refreshed token as fallback');
        }
      } else {
        console.warn('No authenticated Firebase user found when making API request');
      }
    } catch (error) {
      console.error('Error getting auth token:', error);
    }
    
    return config;
  },
  (error) => {
    console.error('Request error:', error);
    return Promise.reject(error);
  }
  );

/**
 * Response interceptor: fills photos under review on success; on failure
 * raises the subscription wall or leaves a refused garden as the module
 * note says, logs a warning, and rethrows the error unchanged.
 */
apiClient.interceptors.response.use(
  (response) => {
    debugLog('ApiClient', 'API Response:', {
      url: response.config.url,
      status: response.status,
      data: response.data
    });
    // A photo the safety check flagged comes with no links: every screen draws
    // "This photo is being reviewed" in its place (shared/lib/underReview).
    fillUnderReview(response.data);
    return response;
  },
  (error) => {
    // The paused plan's wall. The backend says 402 with this code for a
    // write while a monthly plan is paused (Free writes, within its limits);
    // the dialog that answers it lives in the layout, so whoever made the
    // call need not know about billing at all.
    if (error.response?.status === 402 && error.response?.data?.error === 'SUBSCRIPTION_REQUIRED') {
      window.dispatchEvent(new CustomEvent(SUBSCRIPTION_REQUIRED_EVENT));
    }

    // Free's plants all growing: the same dialog, saying what Free keeps and
    // offering Subscribe (never "trial ended"). Where several plants are added
    // at once the sheet offers what fits before this can happen (FreeRoom).
    if (error.response?.status === 402 && error.response?.data?.error === 'FREE_PLANT_LIMIT') {
      window.dispatchEvent(new CustomEvent(SUBSCRIPTION_REQUIRED_EVENT, {
        detail: { kind: 'plants', limit: Number(error.response.data.limit ?? 5) },
      }));
    }

    /*
     * The same wall, different sentence. Identifying a photograph is refused
     * on its own code because "your trial has ended" would be wrong: the
     * trial may be running perfectly well and this one feature still costs
     * us money per use. Without this the request failed silently and the
     * gardener was left looking at a button that did nothing.
     */
    if (error.response?.status === 402 && error.response?.data?.error === 'SUBSCRIPTION_REQUIRED_AI') {
      window.dispatchEvent(new CustomEvent(SUBSCRIPTION_REQUIRED_EVENT, { detail: { ai: true } }));
    }

    /*
     * The garden this browser is tending refused them: they were shown out,
     * left it in another tab, or someone else signed in here. Every garden
     * request would be refused from now on, and the switcher back goes with
     * the membership, so the app returns to their own garden and says why
     * (gardenMembers). The refused request still fails for its caller.
     */
    if (error.response?.status === 403 && error.response?.data?.error === 'NOT_A_MEMBER') {
      leaveRefusedGarden();
    }
    // E16: while an admin is viewing as a gardener the server refuses every change;
    // its refusal sentence is shown, since the buttons themselves are left as they are.
    const refusal = error.response?.data?.error;
    if (viewingAs() && refusal === 'VIEW_AS_READ_ONLY') {
      // A beat later, so it is the toast left showing after the screen's own failure words.
      const said = error.response?.data?.message;
      const sentence = typeof said === 'string' && said.trim() ? said : t('viewAs.refused');
      setTimeout(() => toast({ title: sentence, variant: 'destructive' }), 150);
    } else if (viewingAs() && ['VIEW_AS_NOT_ADMIN', 'VIEW_AS_NO_GARDENER', 'VIEW_AS_BAD_ID'].includes(refusal)) {
      // The view itself was refused: the view is cleared and the admin is taken back to the picker.
      endRefusedViewAs();
    }
    // warn, not error: console.error is forwarded to Sentry, and a phone
    // with no signal would send one report per request. A failed call is
    // handled where it was made; the backend reports its own faults.
    console.warn('API Error Details:', {
      url: error.config?.url,
      status: error.response?.status,
      statusText: error.response?.statusText,
      data: error.response?.data,
      message: error.message
    });
    return Promise.reject(error);
  }
);